import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { Task, Project } from '@/types/product-manager';
import { updateTask } from '@/services/productManagerService';
import { getAllUserAppAccess } from '@/services/appAccess';
import { employeeNamesService } from '@/services/employeeNames';
import { Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { productManagerEmailService } from '@/services/productManagerEmailService';
import { useAuth } from '@/context/AuthContext';
import ImageUpload from './ImageUpload';
import ImageGallery from './ImageGallery';
import { ImageUploadResult } from '@/services/productManagerImageService';
import { useModalClipboardPaste } from '@/hooks/useModalClipboardPaste';

interface EditTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: Task;
  projects: Project[];
  onTaskUpdated: () => void;
}

interface UserInfo {
  email: string;
  displayName: string;
}

const EditTaskDialog: React.FC<EditTaskDialogProps> = ({
  open,
  onOpenChange,
  task,
  projects,
  onTaskUpdated
}) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [status, setStatus] = useState<'todo' | 'in-progress' | 'completed'>('todo');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<ImageUploadResult[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [imageUploadError, setImageUploadError] = useState<string>('');

  useEffect(() => {
    if (open && task) {
      // Populate form with task data
      setTitle(task.title);
      setDescription(task.description);
      setProjectId(task.projectId);
      setPriority(task.priority);
      setStatus(task.status);
      setAssignedTo(task.assignedTo || 'unassigned');
      setDueDate(task.dueDate ? (task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate as any)) : undefined);
      setTags(task.tags?.join(', ') || '');
      
      // Set existing images
      setExistingImages(task.images || []);
      setUploadedImages([]);
      setImageUploadError('');
      
      loadUsers();
    }
  }, [open, task]);

  const loadUsers = async () => {
    try {
      const allUsers = await getAllUserAppAccess();
      const productManagerUsers = allUsers.filter(user =>
        user.allowedApps.includes('product-manager')
      );
      
      const userInfos = await Promise.all(
        productManagerUsers.map(async (user) => {
          const displayName = await employeeNamesService.getNameFromEmail(user.email);
          return {
            email: user.email,
            displayName: displayName || user.email
          };
        })
      );
      
      setUsers(userInfos);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    setDueDate(date);
    // Close the popover after date selection
    setIsDatePickerOpen(false);
  };

  const handleImagesUploaded = (images: ImageUploadResult[]) => {
    setUploadedImages(prev => [...prev, ...images]);
    setImageUploadError('');
  };

  const handleImageUploadError = (error: string) => {
    setImageUploadError(error);
  };

  const handleExistingImageDeleted = (imageUrl: string) => {
    setExistingImages(prev => prev.filter(url => url !== imageUrl));
  };

  const handleNewImageDeleted = (imageUrl: string) => {
    setUploadedImages(prev => prev.filter(img => img.url !== imageUrl));
  };

  // Handle clipboard paste - convert File[] to ImageUploadResult[] format
  const handleClipboardPaste = async (files: File[]) => {
    try {
      // Import the upload function
      const { uploadTaskImage } = await import('@/services/productManagerImageService');
      
      // Upload each file and convert to ImageUploadResult format
      const uploadPromises = files.map(async (file) => {
        const result = await uploadTaskImage(task.id, file);
        return result;
      });
      
      const uploadResults = await Promise.all(uploadPromises);
      handleImagesUploaded(uploadResults);
    } catch (error) {
      console.error('Error uploading clipboard images:', error);
      handleImageUploadError('Failed to upload clipboard images');
    }
  };

  // Modal-level clipboard paste functionality
  const { modalRef } = useModalClipboardPaste({
    onImagesPasted: handleClipboardPaste,
    onError: handleImageUploadError,
    maxImages: 3,
    disabled: loading,
    enablePaste: open
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    try {
      setLoading(true);

      // Find images that were removed (existed in original task but not in current existingImages)
      const originalImages = task.images || [];
      const removedImages = originalImages.filter(img => !existingImages.includes(img));
      
      // Delete removed images from Firebase Storage
      if (removedImages.length > 0) {
        try {
          const { deleteImage } = await import('@/services/productManagerImageService');
          await Promise.all(removedImages.map(imageUrl => deleteImage(imageUrl)));
        } catch (imageDeleteError) {
          console.error('Error deleting removed images:', imageDeleteError);
          // Don't fail the task update if image deletion fails
        }
      }

      // Build updates object without undefined values
      const updates: any = {
        title: title.trim(),
        description: description.trim(),
        projectId,
        priority,
        status,
        updatedAt: Timestamp.now()
      };

      // Only add assignedTo if it has a value
      if (assignedTo && assignedTo !== 'unassigned') {
        updates.assignedTo = assignedTo;
      }

      // Only add dueDate if it's set
      if (dueDate) {
        updates.dueDate = Timestamp.fromDate(dueDate);
      }

      // Only add tags if there are any
      if (tags.trim()) {
        const tagArray = tags.split(',').map(tag => tag.trim()).filter(Boolean);
        if (tagArray.length > 0) {
          updates.tags = tagArray;
        }
      }

      // Update images - combine existing and newly uploaded
      const allImages = [...existingImages, ...uploadedImages.map(img => img.url)];
      if (allImages.length > 0) {
        updates.images = allImages;
      } else {
        // If no images, explicitly set to empty array to clear the field
        updates.images = [];
      }

      // Only add completedAt if moving to completed status
      if (status === 'completed' && task.status !== 'completed') {
        updates.completedAt = Timestamp.now() as any;
      }

      await updateTask(task.id, updates);
      
      // Send email notifications for relevant changes
      if (user?.email) {
        try {
          const selectedProject = projects.find(p => p.id === projectId);
          const projectName = selectedProject?.name || 'Unknown Project';
          
          // Check if assignment changed and send notification to new assignee
          if (assignedTo && assignedTo !== 'unassigned' && assignedTo !== task.assignedTo) {
            await productManagerEmailService.sendTaskAssignedNotification(
              assignedTo,
              task.id,
              title,
              projectName,
              user.email
            );
          }
          
          // Check if task was completed and notify the creator
          if (status === 'completed' && task.status !== 'completed' && task.createdBy) {
            await productManagerEmailService.sendTaskCompletedNotification(
              task.createdBy,
              task.id,
              title,
              projectName,
              user.email
            );
          }
          
          // Check if status changed and send status change notification to assignee
          if (status !== task.status && task.assignedTo) {
            const statusDisplayName = status.replace('-', ' ');
            await productManagerEmailService.sendTaskStatusChangedNotification(
              task.assignedTo,
              task.id,
              title,
              projectName,
              user.email,
              statusDisplayName
            );
          }
          
          // Send general update notification to assignee (if task is assigned and other fields changed)
          else if (task.assignedTo && (
            title !== task.title ||
            description !== task.description ||
            priority !== task.priority
          )) {
            await productManagerEmailService.sendTaskUpdatedNotification(
              task.assignedTo,
              task.id,
              title,
              projectName,
              user.email
            );
          }
        } catch (emailError) {
          console.error('Error sending email notification:', emailError);
          // Don't fail the task update if email fails
        }
      }
      
      onTaskUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={modalRef} className="sm:max-w-[600px] max-h-[90vh] bg-gray-50 dark:bg-gray-900 z-50 overflow-hidden flex flex-col" style={{ zIndex: 9999 }}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Edit Task</DialogTitle>
          <DialogDescription>
            Update the task details below.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter task title"
              required
              className="bg-white dark:bg-gray-800"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter task description"
              className="min-h-[100px] bg-white dark:bg-gray-800"
            />
          </div>

          {/* Project and Status Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="project">Project</Label>
              <Input
                id="project"
                value={projects.find(p => p.id === projectId)?.name || 'Unknown Project'}
                disabled
                className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={(value: 'todo' | 'in-progress' | 'completed') => setStatus(value)}>
                <SelectTrigger className="bg-white dark:bg-gray-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">To Do</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Priority and Assignee Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={priority} onValueChange={(value: 'low' | 'medium' | 'high' | 'critical') => setPriority(value)}>
                <SelectTrigger className="bg-white dark:bg-gray-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="assignee">Assign To</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger className="bg-white dark:bg-gray-800">
                  <SelectValue placeholder="Select assignee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.email} value={user.email}>
                      {user.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Due Date</Label>
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700",
                    !dueDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, 'PPP') : 'Select due date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 z-50"
                align="start"
                style={{ zIndex: 9999 }}
              >
                <Calendar
                  mode="single"
                  selected={dueDate || undefined}
                  onSelect={handleDateSelect}
                  initialFocus
                  className="bg-white dark:bg-gray-800 pointer-events-auto [&_button:hover]:!bg-gray-100 [&_button:hover]:dark:!bg-gray-700 [&_button:hover]:!text-gray-900 [&_button:hover]:dark:!text-gray-100"
                  style={{ pointerEvents: 'auto' }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Enter tags separated by commas"
              className="bg-white dark:bg-gray-800"
            />
            <p className="text-xs text-gray-500">Separate multiple tags with commas</p>
          </div>

          {/* Existing Images */}
          {existingImages.length > 0 && (
            <div className="space-y-2">
              <Label>Current Images</Label>
              <ImageGallery
                images={existingImages}
                onImageDeleted={handleExistingImageDeleted}
                canDelete={true}
                showDownload={true}
                deleteFromStorage={false}
              />
            </div>
          )}

          {/* Image Upload Section */}
          <div className="space-y-2">
            <Label>Add Images</Label>
            <ImageUpload
              entityType="task"
              entityId={task.id}
              onImagesUploaded={handleImagesUploaded}
              onError={handleImageUploadError}
              disabled={loading}
              maxImages={3}
              enableClipboardPaste={true}
            />
            
            {imageUploadError && (
              <p className="text-sm text-red-500">{imageUploadError}</p>
            )}
            
            {uploadedImages.length > 0 && (
              <div className="space-y-2">
                <Label>New Images</Label>
                <ImageGallery
                  images={uploadedImages.map(img => img.url)}
                  onImageDeleted={handleNewImageDeleted}
                  canDelete={true}
                  showDownload={false}
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !title.trim()}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {loading ? 'Updating...' : 'Update Task'}
            </Button>
          </div>
        </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditTaskDialog;